/**
 * Shared, browser-safe types and URL validation for the free SEO audit.
 * The fetching/parsing implementation lives in `site-audit.server.ts`.
 */

export type CheckStatus = "pass" | "warn" | "fail" | "info";

export type AuditCheck = {
  label: string;
  status: CheckStatus;
  /** What was actually observed (e.g. the title text, the H1 count). */
  found: string;
  /** Why it matters / what to do. */
  note: string;
};

export type AuditResult = {
  url: string;
  finalUrl: string;
  redirects: number;
  statusCode: number;
  ms: number;
  bytes: number;
  score: number;
  passed: number;
  total: number;
  checks: AuditCheck[];
};

export type AuditFailure = { error: string };

export const MAX_BYTES = 2_000_000;
export const TIMEOUT_MS = 12_000;

const PRIVATE_HOST =
  /^(localhost$|127\.|10\.|192\.168\.|169\.254\.|0\.0\.0\.0$|\[?::1\]?$|172\.(1[6-9]|2\d|3[01])\.)|\.(local|internal|localhost)$/i;

/** Rejects non-http(s) schemes and anything pointing at private network space. */
export function normalizeAuditUrl(raw: string): { url: URL } | { error: string } {
  let candidate = raw.trim();
  if (!candidate) return { error: "Enter a URL to audit." };
  if (!/^https?:\/\//i.test(candidate)) candidate = "https://" + candidate;
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return { error: "That doesn't look like a valid URL. Try yourdomain.com" };
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return { error: "Only http:// and https:// URLs can be audited." };
  }
  if (PRIVATE_HOST.test(url.hostname) || !url.hostname.includes(".")) {
    return { error: "That host isn't publicly reachable, so there's nothing to audit." };
  }
  if (url.port && url.port !== "80" && url.port !== "443") {
    return { error: "Only standard web ports (80 and 443) can be audited." };
  }
  if (isPrivateIpLiteral(url.hostname)) {
    return { error: "That host isn't publicly reachable, so there's nothing to audit." };
  }
  return { url };
}

/** True for IPv4/IPv6 literals that point at loopback, private, link-local or reserved space. */
export function isPrivateIpLiteral(host: string): boolean {
  const h = host.replace(/^\[|\]$/g, "").toLowerCase();

  const v4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(h);
  if (v4) {
    const [a, b] = [Number(v4[1]), Number(v4[2])];
    if ([a, b, Number(v4[3]), Number(v4[4])].some((n) => Number.isNaN(n) || n > 255)) return true;
    if (a === 0 || a === 10 || a === 127) return true;
    if (a === 169 && b === 254) return true; // link-local / cloud metadata
    if (a === 172 && b >= 16 && b <= 31) return true;
    if (a === 192 && b === 168) return true;
    if (a === 192 && b === 0) return true;
    if (a === 100 && b >= 64 && b <= 127) return true; // CGNAT
    if (a >= 224) return true; // multicast + reserved
    return false;
  }

  if (h.includes(":")) {
    if (h === "::" || h === "::1") return true;
    if (h.startsWith("fe80") || h.startsWith("fc") || h.startsWith("fd")) return true;
    // IPv4-mapped / IPv4-compatible addresses
    const mapped = /(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})$/.exec(h);
    if (mapped) return isPrivateIpLiteral(mapped[1]!);
    // Global unicast is 2000::/3; anything outside it is reserved/local.
    return !/^[23]/.test(h);
  }

  return false;
}
